'use client'

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import clsx from 'clsx'
import {
  ButtonHTMLAttributes,
  ReactNode,
  useCallback,
  useRef,
  useState,
  type JSX,
} from 'react'
import { create } from 'zustand'
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection'
import Icon from '../Icon/Icon'
import type { FaqCategory, FaqItem } from './faq-data'
import { ALL_CATEGORIES_VALUE, FAQ_CATEGORIES } from './faq-data'

export interface HelpCenterItem {
  id: string
  title: string
  content?: ReactNode | string
}

export interface HelpCenterProps {
  /**
   * The items to be displayed in the Help Center
   */
  items: HelpCenterItem[]
}

export interface HelpCenterButtonRenderProps {
  onClick: React.MouseEventHandler
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type']
}

export interface HelpCenterButtonProps {
  /**
   * The Item ID of Help Center Item to expand
   */
  item?: string
  /**
   * A function that renders the intended button for the help center trigger
   */
  render: (buttonProps: HelpCenterButtonRenderProps) => JSX.Element
}

export interface HelpCenterStore {
  items: HelpCenterProps['items']
  dialogRendered: boolean
  dialogVisible: boolean
  expandedItem: string
  expandItem: (itemID: string) => void
  hideDialog: () => void
  setDialogRendered: () => void
  setItems: (items: HelpCenterProps['items']) => void
}

export const useHelpCenter = create<HelpCenterStore>((set) => ({
  items: [],
  dialogRendered: false,
  dialogVisible: false,
  expandedItem: '',
  expandItem: (item: string) =>
    set({ expandedItem: item, dialogVisible: true }),
  hideDialog: () => set({ dialogVisible: false }),
  setDialogRendered: () => set({ dialogRendered: true }),
  setItems: (items) => set({ items }),
}))

export const HelpCenterButton = ({
  item = '',
  render,
}: HelpCenterButtonProps) => {
  const expandItem = useHelpCenter((state) => state.expandItem)

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      expandItem(item)
      e.preventDefault()
    },
    [item, expandItem],
  )

  return render({ onClick, type: 'button' })
}

const itemContentClassnames = `[&_p]:type-body-8 [&_p:not(:first-child)]:mt-6 [&_img]:mb-6 [&_img]:rounded-8 [&_img]:border [&_img]:border-neutral-400 [&_img]:overflow-hidden`
const itemContentLinkClassnames = `[&_p:has(a:only-child)_a]:flex [&_p:has(a:only-child)_a]:underline [&_p:has(a:only-child)_a]:type-body-5 [&_p:has(a:only-child)_a]:h-8 [&_p:has(a:only-child)_a]:lg:h-12 [&_p:has(a:only-child)_a]:items-center [&_p:has(a:only-child)_a]:underline-offset-[0.3em]`

function FaqAccordionItem({ item }: { item: FaqItem }) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="border-b border-neutral-400">
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between py-4 text-left type-body-8 font-medium transition-colors hover:text-theme-secondary"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="pr-4">{item.question}</span>
        <Icon
          icon={isExpanded ? 'chevron-up' : 'chevron-down'}
          className="w-4 h-4 shrink-0 text-neutral-600 transition-transform duration-200"
        />
      </button>
      <div
        className={clsx(
          'grid transition-[grid-template-rows] duration-200',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <p className="pb-4 type-body-8 leading-relaxed text-neutral-700">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  )
}

function FaqSection({ category }: { category: FaqCategory }) {
  return (
    <div className="mb-6">
      <h3 className="mb-2 type-body-5 font-semibold">{category.label}</h3>
      <div>
        {category.items.map((item) => (
          <FaqAccordionItem key={item.question} item={item} />
        ))}
      </div>
    </div>
  )
}

export default function HelpCenter({ items }: HelpCenterProps) {
  const [expandedItem, isOpen, hide] = useHelpCenter((state) => [
    state.expandedItem,
    state.dialogVisible,
    state.hideDialog,
  ])
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES_VALUE)

  const filteredCategories =
    selectedCategory === ALL_CATEGORIES_VALUE
      ? FAQ_CATEGORIES
      : FAQ_CATEGORIES.filter((cat) => cat.value === selectedCategory)

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <Transition
      show={isOpen}
      enter="ease-out duration-100"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200 delay-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Dialog
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={hide}
        initialFocus={closeButtonRef}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-theme-primary/10"
            aria-hidden="true"
          />
          {/* Panel */}
          <div className="relative flex min-h-full w-full items-stretched justify-end">
            <TransitionChild
              enter="ease-out duration-300 delay-100"
              enterFrom="opacity-0 translate-x-full"
              enterTo="opacity-100 translate-x-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-x-0"
              leaveTo="opacity-0 translate-x-full"
            >
              <DialogPanel className="w-full md:max-w-[475px] bg-white py-14 px-6 overflow-y-auto">
                <DialogTitle as="h2" className="type-body-1">
                  Help Center
                </DialogTitle>
                {items.length > 0 && (
                  <div className="flex flex-col gap-y-6 mt-8">
                    {items.map(({ id, title, content }) => (
                      <CollapsibleSection
                        defaultOpen={expandedItem === id}
                        key={id}
                        title={title}
                        sectionClassName="border-b border-neutral-400 w-full"
                        overrideSectionClassName
                        buttonClassName="px-0"
                        panelClassName={clsx(
                          'pb-4 text-neutral-700',
                          itemContentClassnames,
                          itemContentLinkClassnames,
                        )}
                        overridePanelClassName
                        buttonTextClassName="text-left"
                      >
                        {content}
                      </CollapsibleSection>
                    ))}
                  </div>
                )}
                <div className="mt-6">
                  <label className="block mb-2 type-body-8 font-medium">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full rounded-lg border border-neutral-400 px-4 py-3 type-body-8 bg-white mb-6 focus:outline-none focus:ring-2 focus:ring-theme-secondary"
                  >
                    <option value={ALL_CATEGORIES_VALUE}>All Topics</option>
                    {FAQ_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                  {filteredCategories.map((category) => (
                    <FaqSection key={category.value} category={category} />
                  ))}
                </div>
                <button
                  className="w-full mt-8 px-6 py-3 bg-theme-primary text-white rounded-lg md:hidden"
                  onClick={hide}
                >
                  Close
                </button>
                <button
                  className="w-6 h-6 flex items-center justify-center absolute right-6 top-6"
                  onClick={hide}
                  ref={closeButtonRef}
                >
                  <Icon icon="close" className="w-full h-full" />
                </button>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
